import React, { useState } from 'react';

import styled from '@emotion/styled';

import { FontWeight } from '@clutter/clean';

import { formatCurrency } from '@utils/formatters';

import chevronDown from '@images/checkout/challenger/chevron_down.svg';

import { DisposalPricingModal } from './disposal_pricing_modal';
import { CHALLENGER as T } from './tokens';

let disposalDetailsId = 0;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[4]};
  width: 100%;
`;

const Line = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${T.space[8]};
  padding: 2px 0;
  width: 100%;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${T.color.textSecondary};
`;

const Toggle = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  /* Figma 707:10647 — 2px between “Disposal” and icon */
  gap: 2px;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font: inherit;
  color: inherit;
  text-align: left;

  &:hover {
    opacity: 0.85;
  }
`;

/** Figma 707:10650 icon/keyboard_arrow_down — 20×20 hit box. */
const ChevronBox = styled.span<{ $open: boolean }>`
  position: relative;
  display: block;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  overflow: hidden;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
  transition: transform 150ms ease;
`;

/** Figma leaf: left/right 25%, top/bottom ~34.56% → 10×6.175 glyph. */
const ChevronGlyph = styled.img`
  position: absolute;
  top: 34.56%;
  bottom: 34.56%;
  left: 25%;
  right: 25%;
  width: 10px;
  height: 6.175px;
  display: block;
`;

/**
 * Figma 707:12548 / 707:12550 — Regular 12/20 text-secondary, pr 24.
 * Link: Demi Bold brand “Pricing and restrictions”.
 */
const Details = styled.div`
  margin: 0;
  padding: 0 24px 0 0;
  width: 100%;
  box-sizing: border-box;
  font-family: ${T.font.sans};
  font-size: 12px;
  font-weight: ${FontWeight.Regular};
  line-height: 20px;
  color: ${T.color.textSecondary};
`;

const DetailsLink = styled.button`
  appearance: none;
  display: inline;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-family: ${T.font.sans};
  font-size: 12px;
  font-weight: ${FontWeight.Semibold};
  line-height: 20px;
  color: ${T.color.linkBrand};
  text-align: left;

  &:hover {
    opacity: 0.85;
  }
`;

type Props = {
  amount: number;
};

/**
 * Booking-summary Disposal line (Figma 707:10646 / 707:12539).
 * Label + chevron expands the follow-up note (707:12550).
 */
export const DisposalSummaryRow: React.FC<Props> = ({ amount }) => {
  const [detailsId] = useState(
    () => `disposal-summary-details-${++disposalDetailsId}`,
  );
  const [open, setOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);

  return (
    <>
      <Row>
        <Line>
          <Toggle
            type="button"
            aria-expanded={open}
            aria-controls={detailsId}
            aria-label={
              open ? 'Hide disposal details' : 'Show disposal details'
            }
            onClick={() => setOpen((value) => !value)}
          >
            <span>Disposal</span>
            <ChevronBox $open={open} aria-hidden>
              <ChevronGlyph src={chevronDown} alt="" />
            </ChevronBox>
          </Toggle>
          <span>from {formatCurrency(amount)}</span>
        </Line>
        {open && (
          <Details id={detailsId}>
            We&apos;ll follow up via email to see which items you&apos;d like
            disposed of. See{' '}
            <DetailsLink type="button" onClick={() => setPricingOpen(true)}>
              Pricing and restrictions
            </DetailsLink>
            .
          </Details>
        )}
      </Row>
      <DisposalPricingModal
        isOpen={pricingOpen}
        onClose={() => setPricingOpen(false)}
      />
    </>
  );
};
